import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { ContentCopy, Download, PrecisionManufacturing, Refresh, Devices } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import { copyTextToClipboard, promptManualCopy } from '../utils/clipboard';

type RemoteConfig = { idServer: string; relayServer: string; key: string };

const RemoteAssistancePage: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<RemoteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setConfig(null);
    fetch('/rustdesk-config.json', { cache: 'no-store', signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Configurazione non disponibile');
        const data = await response.json();
        if (![data.idServer, data.relayServer, data.key].every((value) => typeof value === 'string' && value.trim())) {
          throw new Error('Configurazione incompleta');
        }
        if (!controller.signal.aborted) setConfig(data);
      })
      .catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [reload]);

  const copy = async (value: string, label: string) => {
    const copied = await copyTextToClipboard(value);
    if (!copied) promptManualCopy(value, label);
    setNotice(copied ? `${label}: copiato negli appunti` : `${label}: copia il testo dalla finestra`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000 }}>
      <PageHeader title="Assistenza Remota" subtitle="Configura RustDesk per usare il server privato CM" />
      <Button startIcon={<Devices />} onClick={() => navigate('/my-computers')} sx={{ mb: 2 }}>Apri I miei PC</Button>
      <Stack spacing={3}>
        {notice && <Alert severity="info" onClose={() => setNotice('')}>{notice}</Alert>}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Installa CM Assistenza</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Scarica il setup per il tuo dispositivo: installa RustDesk 1.4.9 e configura il server privato CM.
              Usa il setup sia sull’HMI sia sul PC di assistenza. Chiudi RustDesk prima di procedere.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <Button component="a" href="/downloads/remote-assistance/CM-Assistenza-Windows-1.4.9-r3.exe" variant="contained" startIcon={<Download />}>
                Scarica per Windows 64 bit
              </Button>
              <Button component="a" href="/downloads/remote-assistance/CM-Assistenza-macOS-1.4.9.pkg" variant="contained" startIcon={<Download />}>
                Scarica per Mac
              </Button>
            </Stack>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Windows: esegui il setup e autorizza l’installazione. Mac: apri il pacchetto, completa l’installazione,
              quindi apri RustDesk da Applicazioni. Il pacchetto Mac riconosce Intel e Apple Silicon e richiede Internet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Il setup CM non è firmato digitalmente: il sistema potrebbe chiedere una conferma del produttore.
              L’app installata mantiene nome e interfaccia RustDesk. Non viene impostata una password condivisa.
            </Typography>
            <Alert severity="info">
              Su Mac autorizza RustDesk in Impostazioni di Sistema → Privacy e sicurezza → Registrazione schermo e Accessibilità.
              Dopo il setup verifica che l’app mostri il server CM e lo stato Pronto prima di collegarti.
            </Alert>
            <Button component="a" href="/downloads/remote-assistance/SHA256SUMS.txt" sx={{ mt: 1 }}>Checksum dei pacchetti</Button>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Impostazioni server RustDesk</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Il setup applica questi valori automaticamente. Per una configurazione manuale usa RustDesk → Impostazioni → Rete.
            </Typography>
            {loading && <CircularProgress aria-label="Caricamento configurazione" />}
            {error && <Alert severity="error">Configurazione non disponibile. Premi Ricarica per riprovare.</Alert>}
            {config && <Stack spacing={2}>
              {[
                { label: 'ID Server', value: config.idServer },
                { label: 'Relay Server', value: config.relayServer },
                { label: 'Key (chiave pubblica)', value: config.key },
              ].map((field) => (
                <Stack key={field.label} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField label={field.label} value={field.value} fullWidth InputProps={{ readOnly: true }} />
                  <Button startIcon={<ContentCopy />} onClick={() => copy(field.value, field.label)} aria-label={`Copia ${field.label}`}>Copia</Button>
                </Stack>
              ))}
              <Typography variant="body2">API Server: lascia vuoto.</Typography>
              <Button variant="contained" startIcon={<ContentCopy />} onClick={() => copy(
                `ID Server: ${config.idServer}\nRelay Server: ${config.relayServer}\nKey: ${config.key}\nAPI Server: (vuoto)`,
                'Configurazione server'
              )}>Copia tutti i parametri</Button>
            </Stack>}
            <Button sx={{ mt: 2 }} startIcon={<Refresh />} disabled={loading} onClick={() => setReload((value) => value + 1)}>Ricarica configurazione</Button>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Come collegarti a una macchina</Typography>
            <Box component="ol" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
              <li>Installa CM Assistenza su entrambi i dispositivi usando i pulsanti sopra.</li>
              <li>Apri RustDesk e verifica lo stato Pronto. Su Mac completa le autorizzazioni richieste.</li>
              <li>Sull’HMI richiedi assistenza per inviare ID e password alla dashboard.</li>
              <li>Apri il dettaglio della macchina e premi “Connetti con RustDesk Desktop” nella sezione Connessione Remota.</li>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Il collegamento si apre nell’app RustDesk Desktop. Il controllo della macchina nel browser non è disponibile.
            </Alert>
            <Button variant="outlined" startIcon={<PrecisionManufacturing />} onClick={() => navigate('/machines')}>Vai alle macchine</Button>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default RemoteAssistancePage;
